import { useEffect, useMemo, useState } from "react";

import { useActiveAccount } from "./useActiveAccount";
import { isIdentityDmUnread, subscribeIdentityDmInbox } from "../lib/directMessaging";
import { isIdentityGroupUnread, subscribeIdentityGroupInbox } from "../lib/identityGroups";

export function useUnreadCount() {
  const { activeAccountId } = useActiveAccount();
  const [dmUnread, setDmUnread] = useState<{ identityId: string; count: number }>({ identityId: "", count: 0 });
  const [groupUnread, setGroupUnread] = useState<{ identityId: string; count: number }>({ identityId: "", count: 0 });

  useEffect(() => {
    if (!activeAccountId) {
      setDmUnread({ identityId: "", count: 0 });
      setGroupUnread({ identityId: "", count: 0 });
      return;
    }

    const identityId = activeAccountId;
    let cancelled = false;

    setDmUnread({ identityId, count: 0 });
    setGroupUnread({ identityId, count: 0 });

    const unsubscribeDm = subscribeIdentityDmInbox(
      identityId,
      (inbox) => {
        if (cancelled || inbox.identityId !== identityId) return;
        const count = inbox.threads.reduce(
          (total, thread) => total + (isIdentityDmUnread(thread, identityId) ? 1 : 0),
          0,
        );
        setDmUnread({ identityId, count });
      },
      () => {
        if (!cancelled) setDmUnread({ identityId, count: 0 });
      }
    );

    const unsubscribeGroups = subscribeIdentityGroupInbox(
      identityId,
      (threads) => {
        if (cancelled) return;
        const count = threads.reduce(
          (total, thread) => total + (isIdentityGroupUnread(thread, identityId) ? 1 : 0),
          0,
        );
        setGroupUnread({ identityId, count });
      },
      () => {
        if (!cancelled) setGroupUnread({ identityId, count: 0 });
      }
    );

    return () => {
      cancelled = true;
      unsubscribeDm();
      unsubscribeGroups();
    };
  }, [activeAccountId]);

  const visibleDmCount = dmUnread.identityId === activeAccountId ? dmUnread.count : 0;
  const visibleGroupCount = groupUnread.identityId === activeAccountId ? groupUnread.count : 0;

  return useMemo(
    () => visibleDmCount + visibleGroupCount,
    [visibleDmCount, visibleGroupCount]
  );
}
